import { forwardRef } from 'react';
import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

/* ────────────────────────────────────────────────────────────────────────────
   Cards.

   `padded` (default) owns the inset, so `CardHeader` / `CardBody` /
   `CardFooter` add rhythm rather than padding. `CardFooter` bleeds to the card
   edge with negative margins so its hairline spans the full width.
   ──────────────────────────────────────────────────────────────────────────── */

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Adds hover lift + `group`, so children can react to the card's hover. */
  interactive?: boolean;
  glow?: 'none' | 'bull' | 'floor';
  padded?: boolean;
  as?: 'div' | 'article';
}

const GLOW = {
  none: '',
  bull: 'shadow-glow-bull',
  floor: 'shadow-glow-floor',
} as const;

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { interactive = false, glow = 'none', padded = true, as = 'div', className, children, ...rest },
  ref,
) {
  const Comp = as as ElementType;
  return (
    <Comp
      ref={ref}
      className={cn(
        'relative rounded-xl border border-line bg-surface transition-all duration-150 ease-swift',
        glow === 'none' ? 'shadow-card' : GLOW[glow],
        padded && 'p-4 sm:p-5',
        interactive &&
          'group cursor-pointer hover:-translate-y-0.5 hover:border-line-strong hover:shadow-lift active:translate-y-0 active:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        className,
      )}
      {...rest}
    >
      {children}
    </Comp>
  );
});

interface SlotProps {
  className?: string;
  children?: ReactNode;
}

export function CardHeader({ className, children }: SlotProps) {
  return <div className={cn('mb-4 flex flex-col gap-1', className)}>{children}</div>;
}

export function CardTitle({ className, children }: SlotProps) {
  return (
    <h3 className={cn('font-display text-base font-semibold leading-tight text-fg', className)}>
      {children}
    </h3>
  );
}

export function CardDescription({ className, children }: SlotProps) {
  return <p className={cn('text-xs leading-relaxed text-muted', className)}>{children}</p>;
}

export function CardBody({ className, children }: SlotProps) {
  return <div className={cn('flex flex-col gap-3', className)}>{children}</div>;
}

/** Bleeds to the card edge so the divider is a true full-width hairline. */
export function CardFooter({ className, children }: SlotProps) {
  return (
    <div
      className={cn(
        '-mx-4 mt-4 flex items-center gap-2 border-t border-line px-4 pt-3.5 sm:-mx-5 sm:px-5',
        className,
      )}
    >
      {children}
    </div>
  );
}
